/**
 * Canonical production origin, used wherever markup has to carry absolute URLs.
 *
 * Deliberately a constant rather than an env value: structured data and sitemaps must point at the
 * production site even when rendered from a preview or beta deploy, which is how `app/robots.ts`
 * and the nine sitemap routes already treat it. `app/robots.ts` reads it from here (via
 * `utils/sitemaps.ts`); the nine sitemap routes still hold their own copies of this literal —
 * worth folding in here when they are next touched.
 */
export const SITE_URL = 'https://theboatdb.com';

/** Resolves an app-relative path against {@link SITE_URL}; absolute inputs pass through unchanged. */
export const toAbsoluteUrl = (path: string): string => new URL(path, SITE_URL).toString();

/**
 * Normalises a hand-entered external address into something usable as a schema.org `url`.
 *
 * Manufacturer `website` values in the CMS are inconsistent — many are bare hostnames such as
 * `www.hanseyachtsag.com`, which is not a valid URL and would be published as-is.
 *
 * @returns an absolute `https://` URL, or `undefined` when the input is not plausibly a hostname —
 * better to omit the property than to guess a scheme for arbitrary text.
 */
export const normalizeExternalUrl = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();

  if (!trimmed) {
    return undefined;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return /^[\w-]+(\.[\w-]+)+([/?#].*)?$/.test(trimmed) ? `https://${trimmed}` : undefined;
};
